import { format, isValid } from 'date-fns/fp';
import { layout } from 'styled-system';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { DateTimePicker } from '../date/DateTimePicker';
import { Icon } from '../icon/Icon';
import { Input } from './Input';
import { themeGet } from '@styled-system/theme-get';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  ${layout}
`;

const DateTimePickerWrapper = styled.div`
  position: absolute;
  width: 236px;
  background-color: ${themeGet('dateInput.backgroundColor')};
  border: ${themeGet('dateInput.border')};
  border-radius: ${themeGet('dateInput.borderRadius')};
  box-shadow: ${themeGet('dateInput.boxShadow')};
  z-index: ${themeGet('dateInput.zIndex')};
`;

const Popover = ({ children, visible, onClose }) => {
  const popoverRef = useRef(null);

  const handleBackDropClick = useCallback(
    event => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        onClose(event);
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleBackDropClick);

    return () => {
      document.removeEventListener('mousedown', handleBackDropClick);
    };
  }, [handleBackDropClick]);

  return <div ref={popoverRef}>{visible && children}</div>;
};

export const DateInput = ({
  value,
  onChange,
  minDate,
  maxDate,
  defaultValue,
  disabled,
  required,
  precision,
  showTimePicker,
  showActionsBar,
  canClearSelection,
  dateFormat,
  isMobile,
  width,
  closeOnChange = true,
  ...restProps
}) => {
  const [visible, setVisible] = useState(false);
  const [currentValue, setCurrentValue] = useState(value || defaultValue);
  const inputValue = isValid(currentValue) ? format(dateFormat, currentValue) : '';

  const handleChange = useCallback(
    event => {
      if (onChange) {
        onChange(event);
      }

      if (value === undefined) {
        setCurrentValue(event.target.value);
      }

      if (closeOnChange) {
        setVisible(false);
      }
    },
    [closeOnChange, onChange, value]
  );

  useEffect(() => {
    if (value !== undefined && value !== currentValue) {
      setCurrentValue(value);
    }
  }, [currentValue, value]);

  return (
    <Wrapper width={width}>
      <Input
        {...restProps}
        width={width}
        value={inputValue}
        onFocus={useCallback(() => !disabled && setVisible(true), [disabled])}
        placeholder={dateFormat}
        suffix={<Icon name="CALENDAR" />}
        disabled={disabled}
        readOnly
      />
      <Popover visible={visible} onClose={useCallback(() => setVisible(false), [])}>
        <DateTimePickerWrapper>
          <DateTimePicker
            value={currentValue}
            onChange={handleChange}
            minDate={minDate}
            maxDate={maxDate}
            required={required}
            precision={precision}
            canClearSelection={canClearSelection}
            showTimePicker={showTimePicker}
            showActionsBar={showActionsBar}
            isMobile={isMobile}
          />
        </DateTimePickerWrapper>
      </Popover>
    </Wrapper>
  );
};

DateInput.defaultProps = {
  defaultValue: null,
  dateFormat: 'yyyy-MM-dd',
  showTimePicker: false,
  showActionsBar: true,
};
